import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import Divider from "components/divider";
import Toggle from "components/form/toggle/Toggle";
import TagInput from "components/tagInput/TagInput";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./facilitySettings.scss";

interface IProps {
  state: Partial<IPutTeeSheetSettings>;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

function parseEmails(value?: string): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(/[,;\n]+/)
    .map(email => email.trim())
    .filter(Boolean);
}

function serializeEmails(emails: string[]): string {
  return emails.join(", ");
}

export default function FacilityNotificationSettings(props: IProps) {
  const { state, updateState } = props;
  const { t } = useTranslation();
  const [onlineStoreDraft, setOnlineStoreDraft] = useState("");
  const [teeSheetDraft, setTeeSheetDraft] = useState("");
  const [reservationDraft, setReservationDraft] = useState("");

  const onlineStoreEmails = parseEmails(state?.online_store_notification_emails);
  const teeSheetEmails = parseEmails(state?.internal_tee_sheet_confirmation_email);
  const reservationEmails = parseEmails(state?.internal_reservation_confirmation_email);

  function addEmails(
    key: "online_store_notification_emails" | "internal_tee_sheet_confirmation_email" | "internal_reservation_confirmation_email",
    existing: string[],
    draft: string,
    setDraft: (value: string) => void,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) {
    if (event.key !== "Enter" && event.key !== ",") {
      return;
    }
    event.preventDefault();
    const next = draft.trim().replace(/,$/, "");
    if (!next || existing.includes(next)) {
      setDraft("");
      return;
    }
    updateState(key, serializeEmails([...existing, next]));
    setDraft("");
  }

  function removeEmail(
    key: "online_store_notification_emails" | "internal_tee_sheet_confirmation_email" | "internal_reservation_confirmation_email",
    existing: string[],
    index: number,
  ) {
    updateState(
      key,
      serializeEmails(existing.filter((_, emailIndex) => emailIndex !== index)),
    );
  }

  return (
    <div className="facility-settings-section p-2">
      <div className="facility-settings-notification-block">
        <Toggle
          id="enable_online_store_notifications"
          checked={state?.enable_online_store_notifications}
          onChange={e => updateState("enable_online_store_notifications", e.target.checked)}
          labelRight={t("Online store notifications")}
          labelSupport={t("Alert recipients by email to review purchases during an online store activity.")}
        />
        {state?.enable_online_store_notifications ? (
          <TagInput
            label={t("Notification email")}
            id="online_store_notification_emails"
            value={onlineStoreDraft}
            onChange={e => setOnlineStoreDraft(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              addEmails(
                "online_store_notification_emails",
                onlineStoreEmails,
                onlineStoreDraft,
                setOnlineStoreDraft,
                e,
              )
            }
            placeholder={t("Enter email address...")}
            tags={onlineStoreEmails}
            onTagClick={index => removeEmail("online_store_notification_emails", onlineStoreEmails, index)}
            helpText={t("Add multiple emails by pressing Enter or comma.")}
          />
        ) : null}
      </div>

      <Divider />

      <div className="facility-settings-notification-block">
        <Toggle
          id="enable_internal_tee_sheet_confirmations"
          checked={state?.enable_internal_tee_sheet_confirmations}
          onChange={e => updateState("enable_internal_tee_sheet_confirmations", e.target.checked)}
          labelRight={t("Tee time confirmations")}
          labelSupport={t("Alert recipients by email when a tee time is booked and confirmed.")}
        />
        {state?.enable_internal_tee_sheet_confirmations ? (
          <TagInput
            label={t("Confirmation email")}
            id="internal_tee_sheet_confirmation_email"
            value={teeSheetDraft}
            onChange={e => setTeeSheetDraft(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              addEmails(
                "internal_tee_sheet_confirmation_email",
                teeSheetEmails,
                teeSheetDraft,
                setTeeSheetDraft,
                e,
              )
            }
            placeholder={t("Enter email address...")}
            tags={teeSheetEmails}
            onTagClick={index => removeEmail("internal_tee_sheet_confirmation_email", teeSheetEmails, index)}
            helpText={t("Add multiple emails by pressing Enter or comma.")}
          />
        ) : null}
      </div>

      <div className="facility-settings-notification-block">
        <Toggle
          id="enable_internal_reservation_confirmations"
          checked={state?.enable_internal_reservation_confirmations}
          onChange={e => updateState("enable_internal_reservation_confirmations", e.target.checked)}
          labelRight={t("Reservation confirmations")}
          labelSupport={t("Alert recipients by email when a customer confirms their reservation.")}
        />
        {state?.enable_internal_reservation_confirmations ? (
          <TagInput
            label={t("Confirmation email")}
            id="internal_reservation_confirmation_email"
            value={reservationDraft}
            onChange={e => setReservationDraft(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              addEmails(
                "internal_reservation_confirmation_email",
                reservationEmails,
                reservationDraft,
                setReservationDraft,
                e,
              )
            }
            placeholder={t("Enter email address...")}
            tags={reservationEmails}
            onTagClick={index =>
              removeEmail("internal_reservation_confirmation_email", reservationEmails, index)
            }
            helpText={t("Add multiple emails by pressing Enter or comma.")}
          />
        ) : null}
      </div>
    </div>
  );
}
